/**
 * Chart-specific styling configurations
 * 
 * Contains Highcharts configurations and chart-related styling
 */

// =============================================================================
// HIGHCHARTS CONFIGURATIONS
// =============================================================================

export const chartConfigs = {
  // Base chart configuration (theme-neutral, text colors handled by getThemedChartConfig)
  base: {
    chart: {
      backgroundColor: 'transparent',
      style: {
        fontFamily: 'IBM Plex Sans, sans-serif'
        // color will be set by theme
      }
    },
    title: {
      style: {
        fontWeight: 'bold',
        fontFamily: 'IBM Plex Sans, sans-serif'
        // color will be set by theme
      }
    },
    xAxis: {
      // grid/line colors will be set by theme
      labels: {
        style: {
          fontFamily: 'IBM Plex Mono, monospace'
          // color will be set by theme
        }
      },
      title: {
        style: {
          fontWeight: 'bold'
          // color will be set by theme
        }
      }
    },
    yAxis: {
      // grid/line colors will be set by theme
      labels: {
        style: {
          fontFamily: 'IBM Plex Mono, monospace'
          // color will be set by theme
        }
      },
      title: {
        style: {
          fontWeight: 'bold'
          // color will be set by theme
        }
      }
    },
    legend: {
      itemStyle: {
        fontFamily: 'IBM Plex Sans, sans-serif'
        // color will be set by theme
      }
      // hover/hidden styles will be set by theme
    },
    tooltip: {
      borderRadius: 8,
      style: {
        fontFamily: 'IBM Plex Sans, sans-serif'
        // colors will be set by theme
      }
    },
    credits: {
      enabled: false
    }
  },

  // Stock chart specific configuration (theme-neutral)
  stock: {
    navigator: {
      height: 80,
      // colors will be set by theme
      handles: {
        width: 8,
        height: 20
        // colors will be set by theme
      },
      xAxis: {
        labels: {
          style: {
            fontSize: '10px'
            // color will be set by theme
          }
        }
      }
    },
    rangeSelector: {
      enabled: false
    },
    scrollbar: {
      enabled: false
    }
  },

  // Color palettes for different chart types
  colorPalettes: {
    primary: ['#025387', '#1E96FF', '#84D1F1', '#20CB6E', '#197B1B', '#496860', '#FEB82C', '#F56424', '#E83F5B', '#002E52'],
    performance: ['#10b981', '#34d399', '#6ee7b7', '#a7f3d0', '#d1fae5'],
    warning: ['#f59e0b', '#fbbf24', '#fcd34d', '#fde68a', '#fef3c7'],
    error: ['#ef4444', '#f87171', '#fca5a5', '#fecaca', '#fee2e2'],
    neutral: ['#6b7280', '#9ca3af', '#d1d5db', '#e5e7eb', '#f3f4f6'],
    multi: ['#025387', '#1E96FF', '#84D1F1', '#20CB6E', '#197B1B', '#496860', '#FEB82C', '#F56424', '#E83F5B', '#002E52']
  },

  // Chart type specific configurations
  types: {
    line: {
      plotOptions: {
        line: {
          lineWidth: 2,
          marker: {
            radius: 3,
            states: {
              hover: {
                radius: 5
              }
            }
          }
        }
      }
    },
    
    area: {
      plotOptions: {
        area: {
          fillOpacity: 0.3,
          lineWidth: 2,
          marker: {
            radius: 3
          }
        }
      }
    },
    
    column: {
      plotOptions: {
        column: {
          borderRadius: 2,
          borderWidth: 0
        }
      }
    },
    
    bar: {
      plotOptions: {
        bar: {
          borderRadius: 2,
          borderWidth: 0
        }
      }
    },
    
    pie: {
      plotOptions: {
        pie: {
          allowPointSelect: true,
          cursor: 'pointer',
          dataLabels: {
            enabled: true,
            style: {
              fontFamily: 'IBM Plex Sans, sans-serif'
              // color will be set by theme
            }
          },
          showInLegend: true
        }
      }
    }
  }
};

// =============================================================================
// CHART WRAPPER STYLES
// =============================================================================

export const chartStyles = {
  // Chart container styles
  container: {
    base: 'w-full h-full',
    withBackground: 'bg-white rounded-lg shadow-sm p-4',
    withBorder: 'border border-gray-200 rounded-lg',
    fullHeight: 'h-96 md:h-[500px]',
  },
  
  // Chart loading states
  loading: {
    container: 'flex items-center justify-center h-64',
    spinner: 'animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full',
    text: 'text-gray-500 mt-2',
  },
  
  // Chart error states
  error: {
    container: 'flex flex-col items-center justify-center h-64 text-gray-500',
    icon: 'w-12 h-12 mb-2',
    text: 'text-center',
  },
  
  // Chart legend styles
  legend: {
    container: 'flex flex-wrap gap-4 justify-center mt-4',
    item: 'flex items-center gap-2',
    indicator: 'w-3 h-3 rounded-full',
    label: 'text-sm text-gray-700',
  },
  
  // Chart toolbar styles
  toolbar: {
    container: 'flex items-center justify-between mb-4',
    title: 'text-lg font-semibold text-gray-900',
    actions: 'flex items-center gap-2',
    button: 'px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50',
  },
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get default color for a series based on index
 */
export const getDefaultColor = (index: number, palette: keyof typeof chartConfigs.colorPalettes = 'multi'): string => {
  const colors = chartConfigs.colorPalettes[palette];
  return colors[index % colors.length];
};

/**
 * Merge chart configurations
 */
export const mergeChartConfig = (baseConfig: any, customConfig: any = {}): any => {
  return {
    ...baseConfig,
    ...customConfig,
    chart: {
      ...baseConfig.chart,
      ...customConfig.chart,
    },
    xAxis: {
      ...baseConfig.xAxis,
      ...customConfig.xAxis,
    },
    yAxis: {
      ...baseConfig.yAxis,
      ...customConfig.yAxis,
    },
    tooltip: {
      ...baseConfig.tooltip,
      ...customConfig.tooltip,
    },
    legend: {
      ...baseConfig.legend,
      ...customConfig.legend,
    },
  };
};

// =============================================================================
// THEME-AWARE CHART CONFIGURATIONS
// =============================================================================

import type { Options } from 'highcharts';
import type { Theme } from './themes';

/**
 * Get chart configuration for a specific theme
 */
export const getThemedChartConfig = (theme: Theme): Partial<Options> => {
  return {
    colors: theme.chart.primary,
    chart: {
      backgroundColor: theme.colors.background.surface,
      plotBackgroundColor: theme.isDark ? 'rgba(15, 23, 42, 0.3)' : 'rgba(248, 250, 252, 0.5)',
      plotBorderWidth: 0,
      plotShadow: false,
      style: {
        fontFamily: 'IBM Plex Sans, sans-serif',
        color: theme.colors.text.primary,
      },
    },
    title: {
      style: {
        color: theme.colors.text.primary,
        fontSize: '16px',
        fontWeight: 'bold',
      },
    },
    subtitle: {
      style: {
        color: theme.colors.text.secondary,
        fontSize: '14px',
      },
    },
    xAxis: {
      gridLineColor: theme.isDark ? 'rgba(71, 85, 105, 0.3)' : 'rgba(226, 232, 240, 0.8)',
      lineColor: theme.colors.border.secondary,
      tickColor: theme.colors.border.secondary,
      labels: {
        style: {
          color: theme.colors.text.secondary,
          fontSize: '12px',
        },
      },
      title: {
        style: {
          color: theme.colors.text.primary,
          fontSize: '13px',
          fontWeight: '600',
        },
      },
    },
    yAxis: {
      gridLineColor: theme.isDark ? 'rgba(71, 85, 105, 0.3)' : 'rgba(226, 232, 240, 0.8)',
      lineColor: theme.colors.border.secondary,
      tickColor: theme.colors.border.secondary,
      labels: {
        style: {
          color: theme.colors.text.secondary,
          fontSize: '12px',
        },
      },
      title: {
        style: {
          color: theme.colors.text.primary,
          fontSize: '13px',
          fontWeight: '600',
        },
      },
    },
    tooltip: {
      backgroundColor: theme.colors.background.elevated,
      borderColor: theme.colors.border.primary,
      borderRadius: 8,
      borderWidth: 1,
      shadow: theme.isDark ? {
        color: 'rgba(0, 0, 0, 0.5)',
        offsetX: 0,
        offsetY: 2,
        opacity: 0.3,
        width: 6
      } : {
        color: 'rgba(0, 0, 0, 0.1)',
        offsetX: 0,
        offsetY: 1,
        opacity: 0.2,
        width: 3
      },
      style: {
        color: theme.colors.text.primary,
        fontSize: '12px',
        fontFamily: 'IBM Plex Sans, sans-serif',
      },
    },
    legend: {
      itemStyle: {
        color: theme.colors.text.primary,
        fontSize: '12px',
        fontFamily: 'IBM Plex Sans, sans-serif',
      },
      itemHoverStyle: {
        color: theme.colors.text.secondary,
      },
      itemHiddenStyle: {
        color: theme.colors.text.muted,
      },
    },
    plotOptions: {
      series: {
        dataLabels: {
          style: {
            color: theme.colors.text.primary,
            fontSize: '11px',
            fontWeight: '500',
            textOutline: theme.isDark ? '1px 1px rgba(15, 23, 42, 0.8)' : '1px 1px rgba(255, 255, 255, 0.8)',
          },
        },
        marker: {
          states: {
            hover: {
              fillColor: theme.isDark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.8)',
              lineColor: theme.colors.text.primary,
              lineWidth: 2,
            },
          },
        },
      },
      line: {
        lineWidth: 2.5,
        marker: {
          radius: 4,
          symbol: 'circle',
        },
      },
      column: {
        borderRadius: theme.isDark ? 3 : 2,
        borderWidth: 0,
        shadow: theme.isDark ? false : {
          color: 'rgba(0, 0, 0, 0.1)',
          offsetX: 0,
          offsetY: 1,
          opacity: 0.3,
          width: 2
        },
      },
      area: {
        fillOpacity: theme.isDark ? 0.4 : 0.3,
        lineWidth: 2,
        marker: {
          radius: 3,
        },
      },
      pie: {
        borderWidth: theme.isDark ? 1 : 0,
        borderColor: theme.isDark ? theme.colors.border.primary : 'transparent',
        shadow: theme.isDark ? false : {
          color: 'rgba(0, 0, 0, 0.1)',
          offsetX: 0,
          offsetY: 2,
          opacity: 0.3,
          width: 4
        },
      },
    },
    // Navigation (for stock charts)
    navigation: {
      buttonOptions: {
        theme: {
          fill: theme.colors.background.elevated,
          stroke: theme.colors.border.primary,
        },
      },
    },
    // Range selector (for stock charts)
    rangeSelector: {
      buttonTheme: {
        fill: theme.colors.background.elevated,
        stroke: theme.colors.border.primary,
      },
      inputBoxBorderColor: theme.colors.border.primary,
      inputBoxWidth: 120,
      inputBoxHeight: 18,
      inputStyle: {
        backgroundColor: theme.colors.background.surface,
        color: theme.colors.text.primary,
        fontSize: '11px',
      },
      labelStyle: {
        color: theme.colors.text.secondary,
        fontSize: '11px',
      },
    },
    // Navigator (for stock charts)
    navigator: {
      maskFill: theme.isDark ? 'rgba(59, 130, 246, 0.15)' : 'rgba(37, 99, 235, 0.15)',
      outlineColor: theme.colors.status.info,
      outlineWidth: 1,
      handles: {
        backgroundColor: theme.colors.status.info,
        borderColor: theme.isDark ? 'rgba(59, 130, 246, 0.8)' : 'rgba(29, 78, 216, 1)',
        width: 8,
        height: 20,
      },
      xAxis: {
        gridLineColor: theme.isDark ? 'rgba(71, 85, 105, 0.2)' : 'rgba(226, 232, 240, 0.6)',
        labels: {
          style: {
            color: theme.colors.text.muted,
            fontSize: '10px',
          },
        },
      },
    },
    // Scrollbar (for stock charts)
    scrollbar: {
      barBackgroundColor: theme.colors.background.secondary,
      barBorderColor: theme.colors.border.primary,
      buttonBackgroundColor: theme.colors.background.elevated,
      buttonBorderColor: theme.colors.border.primary,
      rifleColor: theme.colors.text.muted,
      trackBackgroundColor: theme.colors.background.primary,
      trackBorderColor: theme.colors.border.primary,
    },
    // Credits
    credits: {
      enabled: false,
    },
  };
};

// =============================================================================
// THEME INTEGRATION HOOK
// =============================================================================

/**
 * Custom hook for using themed chart configurations
 * This hook provides easy access to theme-aware Highcharts configurations
 */
export const useThemedChartConfig = () => {
  // Note: This will be updated when we integrate with ThemeProvider
  // For now, we'll add a placeholder that components can use
  
  const getConfig = (customOptions: Partial<Options> = {}): Partial<Options> => {
    // This will be enhanced when we integrate with the actual theme provider
    // For now, return a basic configuration
    const baseConfig = chartConfigs.base;
    return mergeChartConfig(baseConfig, customOptions);
  };

  return {
    getConfig,
    getThemedConfig: getConfig, // Alias for backwards compatibility
  };
};
